import React, { useEffect, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { Link, useHistory } from 'react-router-dom';

import { useQueryState } from 'hooks/useQueryState';

import { useLoginStore } from 'store/login.store';

import { useMutationLogin } from 'api/mutations/login.mutation';
import { useQueryGetQRCode } from 'api/queries/getQRCode.query';
import { useQueryGetUserDetail } from 'api/queries/getUserDetail.query';
import { useQueryServerTime } from 'api/queries/serverTime.query';

import { LoginButton } from 'components/common/LoginButton/LoginButton';

import { GENERIC_ERROR, encrypt } from 'components/App/AuthPublicPages/NewDesign/Login/Login.utils';
import classes from 'components/App/AuthPublicPages/NewDesign/MfaSignUp/MfaSignUp.module.scss';
import { Layout } from 'components/App/AuthPublicPages/components/Layout/Layout';
import { useLoginHook } from 'components/App/AuthPublicPages/hooks/loginHook';
import { typedArrayToURL } from 'components/Dashboards/Project/Components/DataStories/dataStories.utils';
import Button from 'components/DesignSystem/Button/Button';
import TextInput from 'components/DesignSystem/TextInput/TextInput';
import { CustomizedFormErrorMessage } from 'components/common';

const links = {
    google: {
        appStore: 'https://apps.apple.com/us/app/google-authenticator/id388497605',
        googlePlay: 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en',
    },
    microsoft: {
        appStore: 'https://apps.apple.com/us/app/microsoft-authenticator/id983156458',
        googlePlay: 'https://play.google.com/store/apps/details?id=com.azure.authenticator&hl=en&pli=1',
    },
};

export const MfaSignUp = () => {
    const history = useHistory();
    const [type] = useQueryState('type');
    const [emailAfterMfa, setEmailAfterMfa] = useState<string | undefined>(undefined);
    const [error, setError] = useState<string>('');
    const { data: serverTime } = useQueryServerTime();
    const { username, password } = useLoginStore();
    const { mutate: loginMutation, isLoading: isLoadingLogin } = useMutationLogin();
    const { data: user, isLoading: isLoadingUserDetail } = useQueryGetUserDetail({
        email: emailAfterMfa,
    });

    const { data: qrCode } = useQueryGetQRCode({ email: username });

    useLoginHook({ user, isSwitchUser: false });

    const form = useForm<{ mfaCode: string }>({
        defaultValues: {
            mfaCode: '',
        },
    });

    const handleLogin = ({ mfaCode }: { mfaCode: string }) => {
        if (!username || !password) return null;
        const time = serverTime?.time as number;
        const payload =
            time !== 0
                ? { encrypt: encrypt(JSON.stringify({ username, password, mfaCode }), time), time }
                : { username, password, mfaCode };
        loginMutation(payload, {
            onSuccess: (response) => {
                if (response.email) {
                    setEmailAfterMfa(response.email);
                    setError('');
                } else {
                    setError(GENERIC_ERROR);
                }
            },
            onError: () => {
                setError(GENERIC_ERROR);
            },
        });
    };

    useEffect(() => {
        if (!username || !password) {
            history.push('/login');
        }
    }, [username, password]);

    const link: { appStore: string; googlePlay: string } | null =
        type && ['google', 'microsoft'].includes(type) ? links[type] : null;

    return (
        <Layout>
            <div className={classes.container}>
                <div className={classes.title}>Setup two-factor authentication app</div>
                <p>
                    To keep your account secure, Two-Factor Authentication (2FA) is required as part of your
                    organization's security preferences. <br />
                    This additional layer of security helps prevent unauthorized access, even if your password is
                    compromised.
                </p>
                <p>
                    Please follow these steps:
                    <br />
                    1. Download the <span className={classes.authenticator_type}>{type}</span> Authenticator app from
                    the{' '}
                    <a href={link?.appStore} target={'_blank'}>
                        App Store (iOS)
                    </a>{' '}
                    or{' '}
                    <a href={link?.googlePlay} target={'_blank'}>
                        Google Play (Android)
                    </a>
                    , to your mobile device, if you haven’t already.
                    <br />
                    2. Scan the QR code below with the app to link it to your account.
                </p>
                <div
                    className={classes.qrCode}
                    style={{
                        backgroundImage: `url(${typedArrayToURL(qrCode, 'image/*')})`,
                    }}
                />
                <p>3. Enter 6-digit code generated by the app to complete the setup.</p>
                <form onSubmit={form.handleSubmit(handleLogin)} autoComplete={'off'}>
                    <div className={classes.formRows}>
                        <div className={classes.row}>
                            <Controller
                                render={({ field }) => (
                                    <TextInput
                                        {...field}
                                        id={'mfaCode'}
                                        error={Boolean(form.formState.errors.mfaCode)}
                                        maxLength={6}
                                    />
                                )}
                                rules={{ required: 'Code is Required' }}
                                name={'mfaCode'}
                                control={form.control}
                            />
                            {form.formState.errors.mfaCode && (
                                <CustomizedFormErrorMessage text={form.formState.errors.mfaCode.message} />
                            )}
                            {error && <CustomizedFormErrorMessage text={error} />}
                        </div>

                        <div className={classes.row}>
                            <Button
                                variant={'primary'}
                                disabled={isLoadingLogin || isLoadingUserDetail}
                                isLoading={isLoadingLogin || isLoadingUserDetail}
                                id={'login-button'}
                                type={'submit'}
                            >
                                Sign In
                            </Button>
                        </div>
                        <p>
                            Having trouble? <Link to={'/login'}>Retry sign in</Link>, or{' '}
                            <a href={'mailto:cs@foresight.works'}>contact us</a> for help
                        </p>
                    </div>
                </form>
            </div>
        </Layout>
    );
};
